import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  FactCheck as GradeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { loadRemoteProgress, saveRemoteProgress } from '../../api/progressApi';
import { analyzeEvidence } from '../../api/evidenceApi';

const TRACKER_ID = 'dq-math-03-duplicates-practice-bank';

const LENSES = [
  ['detection_rule', 'Detection rule'],
  ['evidence', 'Evidence'],
  ['business_check', 'Business check'],
  ['root_cause', 'Root cause'],
  ['treatment', 'Treatment'],
  ['caution', 'Caution'],
  ['verify', 'Verify'],
  ['learner_sentence', 'Learner sentence'],
];

const WORKING_PROMPTS = {
  '11A': 'Confirm the grain: one row = one booking. Count total rows vs distinct booking_id, and show which id repeats and whether every column matches exactly.',
  '11B': 'Confirm the grain: one row = one donor identity. Count distinct donor_id, then check whether any two rows share an email or postcode despite different IDs and slightly different names.',
  '11C': 'Confirm the grain: one row = one employee. Count distinct employee_id, then show which id repeats and list exactly which columns disagree between the two rows.',
  '11D': 'Confirm the grain: one row = one physical scan event. Count total rows and compare item_id + scanned_at pairs across batch_id to show which scan repeats.',
  '11E': 'Confirm the grain: one row = one payment per subscriber-month. Count distinct payment_id (should equal row count) and distinct subscriber_id, then show that the month column differs on every row.',
  '11F': "Confirm the grain: one row = one customer's current consent decision. Count distinct customer_id, then show which id has more than one row and what differs between those rows (value, source, verified, date).",
  '11G': 'Confirm the grain: one row = one grant payment. Count distinct grant_payment_id vs total rows, then compute the raw summed total and the corrected total after removing the repeat.',
  '11H': 'Confirm the grain separately for each table: is enrolment_id unique? Is course_id unique in the lookup? Show the row count in each table, then explain what happens to the enrolment count after joining to a lookup with a repeated key.',
};

const PRACTICE_TABLES = [
  {
    id: '11A',
    title: 'Practice Table 11A',
    theme: 'Appointment bookings',
    tables: [
      {
        columns: ['booking_id', 'patient_id', 'clinic', 'booked_at'],
        rows: [
          ['B001', 'P101', 'Dental', '2026-07-03 09:00'],
          ['B002', 'P102', 'GP', '2026-07-03 10:30'],
          ['B002', 'P102', 'GP', '2026-07-03 10:30'],
          ['B003', 'P103', 'Physio', '2026-07-03 11:00'],
        ],
      },
    ],
  },
  {
    id: '11B',
    title: 'Practice Table 11B',
    theme: 'Donor database',
    tables: [
      {
        columns: ['donor_id', 'donor_name', 'email', 'postcode'],
        rows: [
          ['D101', 'Michael Osei', 'michael.osei@example.com', 'SE1 8AB'],
          ['D782', 'Mike Osei', 'michael.osei@example.com', 'SE1 8AB'],
          ['D220', 'Fatima Bello', 'fatima@example.com', 'M14 5RT'],
        ],
      },
    ],
  },
  {
    id: '11C',
    title: 'Practice Table 11C',
    theme: 'Employee master',
    tables: [
      {
        columns: ['employee_id', 'full_name', 'role', 'line_manager'],
        rows: [
          ['E100', 'Grace Mensah', 'Analyst', 'T. Cole'],
          ['E101', 'Liam Brown', 'Associate', 'R. Singh'],
          ['E101', 'Liam Brown', 'Senior Associate', 'K. Shah'],
          ['E102', 'Zoe Carter', 'Analyst', 'T. Cole'],
        ],
      },
    ],
  },
  {
    id: '11D',
    title: 'Practice Table 11D',
    theme: 'Warehouse scan feed',
    tables: [
      {
        columns: ['scan_id', 'batch_id', 'item_id', 'scanned_at'],
        rows: [
          ['SC01', 'W88', 'IT10', '2026-07-04 08:01'],
          ['SC02', 'W88', 'IT11', '2026-07-04 08:03'],
          ['SC01', 'W89', 'IT10', '2026-07-04 08:01'],
          ['SC02', 'W89', 'IT11', '2026-07-04 08:03'],
        ],
      },
    ],
  },
  {
    id: '11E',
    title: 'Practice Table 11E',
    theme: 'Subscription payments',
    tables: [
      {
        columns: ['payment_id', 'subscriber_id', 'payment_month', 'amount'],
        rows: [
          ['PAY01', 'S500', '2026-05', '12'],
          ['PAY02', 'S500', '2026-06', '12'],
          ['PAY03', 'S500', '2026-07', '12'],
        ],
      },
    ],
  },
  {
    id: '11F',
    title: 'Practice Table 11F',
    theme: 'Customer marketing consent',
    tables: [
      {
        columns: ['customer_id', 'source', 'email_opt_in', 'updated_at', 'verified'],
        rows: [
          ['C900', 'website', 'true', '2026-07-05', 'false'],
          ['C900', 'compliance', 'false', '2026-06-28', 'true'],
          ['C901', 'compliance', 'true', '2026-07-02', 'true'],
        ],
      },
    ],
  },
  {
    id: '11G',
    title: 'Practice Table 11G',
    theme: 'Grant payment reporting',
    tables: [
      {
        columns: ['grant_payment_id', 'organisation', 'amount'],
        rows: [
          ['GP01', 'Hope Centre', '5000'],
          ['GP02', 'Skills Hub', '7500'],
          ['GP02', 'Skills Hub', '7500'],
          ['GP03', 'Youth Works', '3000'],
        ],
      },
    ],
  },
  {
    id: '11H',
    title: 'Practice Table 11H',
    theme: 'Course enrolment lookup',
    tables: [
      {
        label: 'Enrolments',
        columns: ['enrolment_id', 'course_id', 'learner_id'],
        rows: [
          ['EN01', 'C10', 'L001'],
          ['EN02', 'C20', 'L002'],
        ],
      },
      {
        label: 'Course lookup',
        columns: ['course_id', 'course_name'],
        rows: [
          ['C10', 'Data Foundations'],
          ['C20', 'Excel Analytics'],
          ['C20', 'Spreadsheet Analytics'],
        ],
      },
    ],
  },
];

const RUBRIC_TERMS = {
  '11A': {
    detection: ['4', '3', 'distinct', 'booking_id', 'grain'],
    bucket: ['exact duplicate', 'exact row', 'identical', 'repeated copy'],
    evidence: ['b002', 'same', 'identical', 'repeated'],
    business: ['reception', 'double-click', 'refresh', 'system log', 'double book'],
    treatment: ['keep one', 'remove one', 'drop duplicate', 'remove duplicate', 'dedupe', 'cleaned'],
    caution: ['patient_id', 'all p102', 'delete both', 'blind', 'do not count', 'distinct patient booking', 'distinct booking', 'separate appointment', 'separate booking'],
    verify: ['4', '3', 'row count', 'booking count', 'one row less', '1 row less', 'count after'],
  },
  '11B': {
    detection: ['3', 'distinct', 'donor_id', 'grain'],
    bucket: ['near duplicate', 'fuzzy', 'likely duplicate'],
    evidence: ['michael', 'mike', 'same email', 'postcode', 'se1'],
    business: ['phone', 'confirm', 'contact donor', 'verify identity'],
    treatment: ['review', 'merge', 'standardise', 'confirm'],
    caution: ['name alone', 'blind', 'auto merge'],
    verify: ['donor count', 'master donor', 'history'],
  },
  '11C': {
    detection: ['4', '3', 'distinct', 'employee_id', 'grain'],
    bucket: ['conflicting', 'duplicate key', 'key conflict'],
    evidence: ['e101', 'role', 'manager', 'conflict'],
    business: ['hr', 'source system', 'promotion', 'transfer'],
    treatment: ['survivorship', 'hr', 'source', 'quarantine', 'approved'],
    caution: ['random', 'delete', 'latest only', 'blind'],
    verify: ['one row', 'employee_id', 'headcount', 'role count'],
  },
  '11D': {
    detection: ['4', 'grain', 'item_id', 'scanned_at', 'batch'],
    bucket: ['re-import', 'retry', 'batch', 'load'],
    evidence: ['sc01', 'sc02', 'w88', 'w89', 'batch'],
    business: ['scanner log', 'connectivity', 'retry event', 'warehouse'],
    treatment: ['keep one', 'quarantine', 'idempotent', 'dedupe'],
    caution: ['new scan', 'count twice', 'both batches'],
    verify: ['4', '2', 'scan count', 'batch'],
  },
  '11E': {
    detection: ['distinct', 'payment_id', 'subscriber_id', 'month', 'grain'],
    bucket: ['false positive', 'valid repeat', 'not duplicate'],
    evidence: ['subscriber', 'month', 'different', 'may', 'june', 'july'],
    business: ['billing', 'gateway log', 'charged twice'],
    treatment: ['keep all', 'monthly', 'aggregate', 'subscriber-level'],
    caution: ['subscriber_id', 'delete', 'latest only'],
    verify: ['3', 'payment count', 'subscriber count', 'months'],
  },
  '11F': {
    detection: ['distinct', 'customer_id', 'c900', 'grain'],
    bucket: ['survivorship', 'conflict', 'consent conflict'],
    evidence: ['true', 'false', 'verified', 'compliance', 'website'],
    business: ['compliance team', 'authoritative', 'override'],
    treatment: ['compliance', 'verified', 'survivorship', 'quarantine'],
    caution: ['latest', 'website', 'ignore compliance', 'blind'],
    verify: ['one consent', 'c900', 'marketing', 'opt'],
  },
  '11G': {
    detection: ['4', '3', 'distinct', 'grant_payment_id', 'grain'],
    bucket: ['aggregate impact', 'exact duplicate', 'inflated'],
    evidence: ['gp02', '7500', 'repeated'],
    business: ['bank statement', 'payment system', 'confirm one payment'],
    treatment: ['keep one', 'remove', 'dedupe'],
    caution: ['only row count', 'ignore amount', 'report before'],
    verify: ['23000', '15500', '7500', 'impact', 'total'],
  },
  '11H': {
    detection: ['course_id', 'c20', 'lookup', 'grain', 'enrolment_id'],
    bucket: ['join fan-out', 'fan out', 'lookup duplicate'],
    evidence: ['c20', 'two course', 'lookup', 'excel', 'spreadsheet'],
    business: ['course catalogue', 'rename', 'course system'],
    treatment: ['fix lookup', 'one course', 'effective date', 'resolve'],
    caution: ['sum after join', 'dedupe enrolment', 'ignore lookup'],
    verify: ['2', 'row count', 'joined row', 'enrolment count'],
  },
};

export const DUPLICATE_ANSWER_GUIDE = {
  '11A': {
    detection_rule: 'Grain = one row per appointment booking; booking_id should be unique. Row count = 4, but distinct booking_id count = 3 — B002 appears twice with identical values in every column.',
    evidence: 'B002 appears twice with the same patient, clinic, and booked_at.',
    business_check: 'Confirm with reception/booking-system logs whether B002 was submitted twice by mistake (double-click, page refresh) or genuinely booked twice for a real reason.',
    root_cause: 'Exact duplicate row — byte-identical values across every column.',
    treatment: 'Keep one B002 row, remove the repeated copy, and document the dedupe.',
    caution: 'Do not count both B002 rows as separate appointments or delete all P102 bookings.',
    verify: 'Check row count drops from 4 to 3 and booking count is unchanged apart from the duplicate.',
    learner_sentence: 'B002 is an exact duplicate booking, so keep one copy, remove the repeat, and verify the booking count falls from 4 to 3.',
  },
  '11B': {
    detection_rule: 'Grain = one row per donor identity; the donor master should be unique by confirmed identity, not name alone. Distinct donor_id count = 3, but D101 and D782 share the same email and postcode despite different IDs and a slightly different name.',
    evidence: 'Michael Osei and Mike Osei share the same email and postcode.',
    business_check: "Confirm with the donor by phone or email whether 'Michael' and 'Mike' Osei at the same address are the same person before merging — a shared email/postcode is strong evidence but not certain proof.",
    root_cause: 'Near/fuzzy duplicate — different donor_id and a slightly different name, but matching email and postcode strongly suggest the same person entered twice.',
    treatment: 'Review, confirm the match, standardise the name, and merge into one donor record.',
    caution: 'Do not auto-merge by similar name alone without review.',
    verify: 'Check master donor count and confirm donation/contact history survives the merge.',
    learner_sentence: 'This is a likely fuzzy duplicate because Michael/Mike share email and postcode; confirm and merge, then verify donor count and history.',
  },
  '11C': {
    detection_rule: 'Grain = one row per employee; employee_id should be unique. Row count = 4, distinct employee_id = 3, but E101 appears twice with different role and line_manager values.',
    evidence: 'E101 appears twice with different role and line manager.',
    business_check: "Check with HR or the source system which of the two role/manager values is current — E101's promotion or transfer may have added a new row instead of updating the old one.",
    root_cause: "Conflicting duplicate key — same employee_id, but the two rows disagree on role and manager, so this isn't a copy, it's an unresolved update.",
    treatment: 'Quarantine or resolve via HR/source priority survivorship rule.',
    caution: 'Do not randomly delete one row or blindly keep the latest.',
    verify: 'Confirm E101 has one approved master row and headcount remains correct.',
    learner_sentence: 'E101 is a duplicate key conflict; resolve with HR/source priority, keep one approved record, and verify headcount.',
  },
  '11D': {
    detection_rule: 'Grain = one row per physical scan event; scan_id should be unique per event (or the load should be idempotent). Row count = 4, but the SC01/SC02 pair repeats identically — same item_id, same scanned_at — across batch W88 and W89.',
    evidence: 'SC01 and SC02 repeat across W88 and W89 with the same item/time pattern.',
    business_check: 'Check the warehouse scanner/load logs for a retry event around 2026-07-04 08:01-08:03 — a connectivity retry would explain two identical batches for the same item.',
    root_cause: 'Re-import/retry duplicate — the same scan event was loaded twice under two different batch IDs.',
    treatment: 'Deduplicate or quarantine the repeated retry batch and fix idempotent loading.',
    caution: 'Do not treat both batches as new scans without checking load retry evidence.',
    verify: 'Compare scan count before/after and confirm only the valid scan events remain.',
    learner_sentence: 'The repeated SC01/SC02 pattern suggests a retry duplicate; dedupe the repeated load and verify scan counts.',
  },
  '11E': {
    detection_rule: 'Grain = one row per payment per subscriber-month; payment_id is unique (PAY01-03 all distinct, so row count = distinct payment_id = 3). subscriber_id S500 repeats 3 times, but payment_month differs on every row.',
    evidence: 'S500 appears in May, June, and July as separate monthly payments.',
    business_check: 'Confirm no billing glitch charged S500 twice in the same month — since each payment is a different month, check the payment gateway log to rule that out.',
    root_cause: 'False positive — not actually a duplicate. Same subscriber, but three genuinely different monthly payments.',
    treatment: 'Keep all payments and aggregate at subscriber level only when answering subscriber-level questions.',
    caution: 'Do not dedupe on subscriber_id or keep only the latest month.',
    verify: 'Confirm there are 3 payments but 1 subscriber across 3 months.',
    learner_sentence: 'These are valid monthly repeats, not duplicates; keep all rows and verify payment count by month.',
  },
  '11F': {
    detection_rule: "Grain = one row per customer's current consent decision; customer_id should resolve to one active state. Row count = 3, distinct customer_id = 2, but C900 has two rows with conflicting values recorded on different dates.",
    evidence: 'C900 has true from website (unverified) and false from verified compliance.',
    business_check: 'Confirm with the compliance team which consent record is authoritative — a verified compliance record should normally override an unverified website tick-box.',
    root_cause: 'Survivorship/conflicting-state duplicate — two different consent decisions exist for the same customer, and only one should be treated as current.',
    treatment: 'Apply a survivorship rule that prioritises verified compliance consent, or quarantine for review.',
    caution: 'Do not blindly keep the website/latest value if compliance verification overrides it.',
    verify: 'Confirm C900 has one current consent row and marketing suppression/opt-in logic is correct.',
    learner_sentence: 'C900 has conflicting consent; use verified compliance survivorship and verify one approved consent state.',
  },
  '11G': {
    detection_rule: 'Grain = one row per grant payment; grant_payment_id should be unique. Row count = 4, distinct grant_payment_id = 3 — GP02 (7500) appears twice, so the raw summed total (5000+7500+7500+3000 = 23000) is inflated by 7500 versus the correct total of 15500.',
    evidence: 'GP02 for 7500 appears twice.',
    business_check: 'Check the grant payment system or bank statement for GP02 to confirm only one real payment of 7500 was made to Skills Hub, not two.',
    root_cause: 'Exact duplicate with aggregate impact — a repeated row that silently inflates a summed total.',
    treatment: 'Keep one GP02, remove the repeated row, and quantify the impact.',
    caution: 'Do not report totals before removing the repeated payment.',
    verify: 'Total should reduce by 7500, from 23000 to 15500.',
    learner_sentence: 'GP02 is duplicated and inflates totals by 7500; remove one copy and verify total changes from 23000 to 15500.',
  },
  '11H': {
    detection_rule: "Grain for the enrolment table = one row per enrolment (2 rows, EN01/EN02, both already unique). Grain for the lookup table = one row per course_id, but course_id C20 appears twice in the lookup with two different course_name values — joining will fan EN02 out into 2 rows even though enrolments has no duplicates itself.",
    evidence: 'C20 appears twice in the lookup, so EN02 can join to two course names.',
    business_check: "Check the course catalogue system for why C20 has two names — likely a rename that added a second row instead of updating the first.",
    root_cause: 'Join fan-out duplicate — the enrolment table itself is clean; the duplicate lives in the lookup table and only creates duplicate rows once you join.',
    treatment: 'Fix the lookup so C20 has one valid course row, or add an effective-date rule.',
    caution: 'Do not dedupe the enrolment table or sum after the fan-out join — that hides the real cause.',
    verify: 'Joined result should still have 2 enrolment rows, not a duplicated EN02.',
    learner_sentence: 'The duplicate C20 lookup row creates join fan-out; fix the lookup and verify the joined enrolment count stays 2.',
  },
};

const DUPLICATE_CRITICAL_TERMS = {
  '11A': {
    evidence: ['B002'],
    caution: ['B002'],
    treatment: ['B002'],
    learner_sentence: ['B002'],
  },
  '11C': {
    detection_rule: ['employee_id'],
    evidence: ['E101'],
    verify: ['E101'],
    learner_sentence: ['E101'],
  },
  '11D': {
    evidence: ['SC01', 'SC02'],
    learner_sentence: ['SC01', 'SC02'],
  },
  '11F': {
    evidence: ['C900'],
    verify: ['C900'],
    learner_sentence: ['C900'],
  },
  '11G': {
    detection_rule: ['grant_payment_id'],
    evidence: ['GP02', '7500'],
    treatment: ['GP02'],
    verify: ['23000', '15500'],
    learner_sentence: ['GP02', '7500', '23000', '15500'],
  },
  '11H': {
    detection_rule: ['course_id'],
    evidence: ['C20'],
    treatment: ['C20'],
    verify: ['2'],
    learner_sentence: ['C20'],
  },
};

const emptyResponses = () =>
  PRACTICE_TABLES.reduce((acc, table) => {
    acc[table.id] = LENSES.reduce((lensAcc, [key]) => ({ ...lensAcc, [key]: '' }), { approach_working: '' });
    return acc;
  }, {});

const countCompletedTables = (responses) =>
  PRACTICE_TABLES.reduce((count, table) => {
    const row = responses[table.id] || {};
    const filled = String(row.approach_working || '').trim() && LENSES.every(([key]) => String(row[key] || '').trim());
    return count + (filled ? 1 : 0);
  }, 0);

const missingRequiredFields = (responses) =>
  PRACTICE_TABLES.map((table) => {
    const row = responses[table.id] || {};
    const missing = [];
    if (!String(row.approach_working || '').trim()) missing.push('Approach / working');
    LENSES.forEach(([key, label]) => {
      if (!String(row[key] || '').trim()) missing.push(label);
    });
    return { table, missing };
  }).filter(({ missing }) => missing.length);

const formatMissingFields = (missingTables = []) =>
  missingTables.slice(0, 4).map(({ table, missing }) => `${table.title}: ${missing.join(', ')}`).join(' | ');

const normalise = (value) => String(value || '').toLowerCase();

const hasAny = (value, terms = []) => {
  const text = normalise(value);
  return terms.some((term) => text.includes(term));
};

const lensScore = (value, terms, fallbackMinLength = 16) => {
  const text = normalise(value).trim();
  if (!text) return 0;
  if (hasAny(text, terms)) return 1;
  return text.length >= fallbackMinLength ? 0.5 : 0;
};

const hasCriticalTerm = (value, term) => {
  const text = String(value || '').toLowerCase();
  const token = String(term || '').toLowerCase();
  if (!token) return true;
  return new RegExp(`(^|[^a-z0-9])${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`).test(text);
};

const applyCriticalTermCap = (tableId, key, value, score) => {
  const criticalTerms = DUPLICATE_CRITICAL_TERMS[tableId]?.[key] || [];
  if (!criticalTerms.length || score <= 0) return { score, reason: '' };
  const missingTerms = criticalTerms.filter((term) => !hasCriticalTerm(value, term));
  if (!missingTerms.length) return { score, reason: '' };
  return {
    score: Math.min(score, 0.5),
    reason: `Check exact scenario value: ${missingTerms.join(', ')}.`,
  };
};

const gradeTable = (tableId, answer = {}) => {
  const rubric = RUBRIC_TERMS[tableId];
  if (!rubric) return null;

  const checks = [
    ['detection_rule', 'Detection rule', lensScore(answer.detection_rule, rubric.detection), answer.detection_rule],
    ['evidence', 'Evidence', lensScore(answer.evidence, rubric.evidence), answer.evidence],
    ['business_check', 'Business check', lensScore(answer.business_check, rubric.business), answer.business_check],
    ['root_cause', 'Root cause', lensScore(answer.root_cause, rubric.bucket, 8), answer.root_cause],
    ['treatment', 'Treatment', lensScore(answer.treatment, rubric.treatment), answer.treatment],
    ['caution', 'Caution', lensScore(answer.caution, rubric.caution), answer.caution],
    ['verify', 'Verify', lensScore(answer.verify, rubric.verify), answer.verify],
    ['learner_sentence', 'Learner sentence', lensScore(answer.learner_sentence, [...rubric.bucket, ...rubric.treatment], 40), answer.learner_sentence],
  ].map(([key, label, score, value]) => {
    const capped = applyCriticalTermCap(tableId, key, value, score);
    return [key, label, capped.score, capped.reason];
  });

  const score = checks.reduce((sum, [, , value]) => sum + value, 0);
  const max = checks.length;
  const missing = checks.filter(([, , value]) => value < 1).map(([, label]) => label);
  const strengths = checks.filter(([, , value]) => value >= 1).map(([, label]) => label);
  const lensResults = checks.reduce((acc, [key, label, value, reason]) => ({
    ...acc,
    [key]: {
      label,
      score: value,
      status: value >= 1 ? 'correct' : value > 0 ? 'partial' : 'missed',
      expected: DUPLICATE_ANSWER_GUIDE[tableId]?.[key] || '',
      reason,
    },
  }), {});

  return {
    score,
    max,
    percent: Math.round((score / max) * 100),
    strengths,
    missing,
    lensResults,
    feedback: missing.length
      ? `Review: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}.`
      : 'Strong answer across the duplicate diagnosis pattern.',
  };
};

const semanticGradeTable = async (table, answer = {}) => {
  const fallback = gradeTable(table.id, answer);
  const lenses = LENSES.map(([key, label]) => ({
    key,
    label,
    user_answer: answer[key] || '',
    expected_answer: DUPLICATE_ANSWER_GUIDE[table.id]?.[key] || '',
    critical_terms: DUPLICATE_CRITICAL_TERMS[table.id]?.[key] || [],
  }));
  const content = [
    `Activity: DCC duplicate take-home semantic grading`,
    `Table: ${table.id}`,
    `Theme: ${table.theme}`,
    `Scenario rows: ${JSON.stringify(table.tables)}`,
    `Learner approach/working: ${answer.approach_working || ''}`,
    `Learner answers: ${JSON.stringify(answer)}`,
    `Expected answers: ${JSON.stringify(DUPLICATE_ANSWER_GUIDE[table.id] || {})}`,
  ].join('\n');
  try {
    const result = await analyzeEvidence({
      content,
      analysisType: 'semantic-rubric-grade',
      context: {
        activity_scope: 'daily_concept_clinic_take_home',
        rubric_key: 'data_quality_duplicates',
        table_id: table.id,
        theme: table.theme,
        lenses,
      },
    });
    if (!result?.lensResults) return { ...fallback, mode: fallback?.mode || 'rules' };
    return {
      ...fallback,
      ...result,
      score: Number(result.score ?? fallback.score),
      max: Number(result.max ?? fallback.max),
      percent: Number(result.percent ?? fallback.percent),
      strengths: result.strengths || fallback.strengths,
      missing: result.missing || fallback.missing,
      lensResults: result.lensResults,
      feedback: result.feedback || fallback.feedback,
      mode: result.mode || 'semantic',
    };
  } catch (error) {
    return {
      ...fallback,
      mode: 'rules',
      llm_error: error.message || 'Semantic grader unavailable.',
    };
  }
};

const gradeAllTables = (responses) =>
  PRACTICE_TABLES.reduce((acc, table) => {
    acc[table.id] = gradeTable(table.id, responses[table.id] || {});
    return acc;
  }, {});

const semanticGradeAllTables = async (responses) => {
  const entries = await Promise.all(
    PRACTICE_TABLES.map(async (table) => [table.id, await semanticGradeTable(table, responses[table.id] || {})])
  );
  return entries.reduce((acc, [tableId, grade]) => ({ ...acc, [tableId]: grade }), {});
};

const gradeSummary = (grades = {}) => {
  const entries = Object.values(grades).filter(Boolean);
  if (!entries.length) return null;
  const score = entries.reduce((sum, grade) => sum + grade.score, 0);
  const max = entries.reduce((sum, grade) => sum + grade.max, 0);
  const lensCounts = entries.reduce((acc, grade) => {
    grade.missing.forEach((lens) => {
      acc[lens] = (acc[lens] || 0) + 1;
    });
    return acc;
  }, {});
  const weaknesses = Object.entries(lensCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([lens]) => lens);
  return {
    score,
    max,
    percent: max ? Math.round((score / max) * 100) : 0,
    weaknesses,
  };
};

function DataTable({ table }) {
  const theme = useTheme();
  return (
    <Box sx={{ my: 1.5 }}>
      {table.label && (
        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
          {table.label}
        </Typography>
      )}
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.84rem',
            '& th': {
              bgcolor: alpha(theme.palette.primary.main, 0.09),
              fontWeight: 800,
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              px: 1.25,
              py: 0.85,
              textAlign: 'left',
              verticalAlign: 'top',
            },
          }}
        >
          <thead>
            <tr>
              {table.columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

export default function DuplicatePracticeBank() {
  const theme = useTheme();
  const [responses, setResponses] = useState(() => emptyResponses());
  const [grades, setGrades] = useState({});
  const [loadState, setLoadState] = useState('loading');
  const [saveState, setSaveState] = useState('idle');
  const [gradeState, setGradeState] = useState('idle');
  const [message, setMessage] = useState('');
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const completedCount = useMemo(() => countCompletedTables(responses), [responses]);
  const missingTables = useMemo(() => missingRequiredFields(responses), [responses]);
  const canGrade = loadState !== 'loading' && !missingTables.length;
  const summary = useMemo(() => gradeSummary(grades), [grades]);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    loadRemoteProgress(TRACKER_ID)
      .then((state) => {
        if (cancelled) return;
        const loadedResponses = { ...emptyResponses(), ...(state?.responses || {}) };
        let loadedGrades = state?.grades || {};
        if (Object.keys(loadedGrades).length && Object.values(loadedGrades).some((grade) => grade && !grade.lensResults)) {
          loadedGrades = gradeAllTables(loadedResponses);
        }
        setResponses(loadedResponses);
        setGrades(loadedGrades);
        setLoadState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadState('ready');
        if (!String(error?.message || '').includes('Unable to load')) {
          setMessage(error.message || 'Could not load saved answers.');
        }
      })
      .finally(() => {
        loadedRef.current = true;
      });

    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const saveResponses = async (nextResponses = responses, mode = 'manual', nextGrades = grades) => {
    setSaveState('saving');
    setMessage('');
    try {
      await saveRemoteProgress(TRACKER_ID, {
        version: 1,
        resource: 'dq-math-03-uniqueness-duplicates-sample-tables',
        responses: nextResponses,
        grades: nextGrades,
        grade_summary: gradeSummary(nextGrades),
        completed_tables: countCompletedTables(nextResponses),
        updated_at: new Date().toISOString(),
      });
      setSaveState('saved');
      setMessage(mode === 'auto' ? 'Autosaved.' : 'Saved to your practice record.');
    } catch (error) {
      setSaveState('error');
      setMessage(error.message || 'Could not save your answers.');
    }
  };

  const scheduleSave = (nextResponses, nextGrades = grades) => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState('dirty');
    saveTimerRef.current = setTimeout(() => {
      saveResponses(nextResponses, 'auto', nextGrades);
    }, 900);
  };

  const updateAnswer = (tableId, key, value) => {
    setResponses((current) => {
      const next = {
        ...current,
        [tableId]: {
          ...(current[tableId] || {}),
          [key]: value,
        },
      };
      let nextGrades = grades;
      if (Object.keys(grades).length) {
        nextGrades = { ...grades };
        delete nextGrades[tableId];
        setGrades(nextGrades);
        setMessage(`Table ${tableId} changed. Click Grade answers to run semantic grading again.`);
      }
      scheduleSave(next, nextGrades);
      return next;
    });
  };

  const gradeResponses = async () => {
    if (!canGrade) {
      setMessage(`Finish every table before grading. Still missing: ${formatMissingFields(missingTables)}${missingTables.length > 4 ? ' | ...' : ''}`);
      return;
    }
    setGradeState('grading');
    setMessage('Semantic grading in progress...');
    try {
      const nextGrades = await semanticGradeAllTables(responses);
      setGrades(nextGrades);
      await saveResponses(responses, 'manual', nextGrades);
      const usedAi = Object.values(nextGrades).some((grade) => grade?.mode === 'llm');
      setMessage(usedAi ? 'Semantic grading complete.' : 'Grading complete using the rule fallback. AI semantic grader was unavailable.');
    } finally {
      setGradeState('idle');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.035),
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            Learner Practice: Savable Duplicate Tables
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Show the grain and row-count working first, then decide whether each repeat is an exact duplicate, a fuzzy match, a conflicting key, or a valid repeat. Your work autosaves to your own practice record.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">
            {completedCount}/{PRACTICE_TABLES.length} complete
          </Typography>
          {summary && (
            <Chip
              label={`${summary.percent}%`}
              color={summary.percent >= 75 ? 'success' : summary.percent >= 50 ? 'warning' : 'default'}
              size="small"
              sx={{ fontWeight: 800 }}
            />
          )}
          <Button
            variant="outlined"
            startIcon={<GradeIcon />}
            onClick={gradeResponses}
            disabled={saveState === 'saving' || loadState === 'loading' || gradeState === 'grading' || !canGrade}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {gradeState === 'grading' ? 'Grading...' : 'Grade answers'}
          </Button>
          <Button
            variant="contained"
            startIcon={saveState === 'saving' ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
            onClick={() => saveResponses()}
            disabled={saveState === 'saving' || loadState === 'loading'}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {saveState === 'saving' ? 'Saving...' : 'Save now'}
          </Button>
        </Stack>
      </Stack>

      {message && (
        <Alert severity={saveState === 'error' ? 'error' : 'success'} sx={{ mt: 1.5 }}>
          {message}
        </Alert>
      )}

      {missingTables.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Grade answers unlocks after all tables are attempted. Still missing: {formatMissingFields(missingTables)}{missingTables.length > 4 ? ' | ...' : ''}
        </Alert>
      )}

      {summary && (
        <Alert severity={summary.percent >= 70 ? 'success' : 'info'} sx={{ mt: 1.5 }}>
          Score: {summary.score}/{summary.max} ({summary.percent}%).
          {summary.weaknesses.length
            ? ` Main development areas: ${summary.weaknesses.join(', ')}.`
            : ' No major weak lens detected.'}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 1.5 }}>
        Start each table by naming the grain (what one row should represent) and computing row count vs distinct key count. Then show which values actually differ between the repeated rows before filling the Lens.
      </Alert>

      {loadState === 'loading' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading your saved answers...</Typography>
        </Stack>
      ) : (
        PRACTICE_TABLES.map((practice) => (
          <Box
            key={practice.id}
            sx={{
              mt: 3,
              p: { xs: 1.5, sm: 2 },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
              {practice.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.25 }}>
              <strong>Theme:</strong> {practice.theme}.
            </Typography>
            {grades[practice.id] && (
              <Alert severity={grades[practice.id].percent >= 70 ? 'success' : 'info'} sx={{ mb: 1.5 }}>
                {practice.title} score: {grades[practice.id].score}/{grades[practice.id].max} ({grades[practice.id].percent}%). {grades[practice.id].feedback}
              </Alert>
            )}

            {practice.tables.map((table, index) => (
              <DataTable key={`${practice.id}-${index}`} table={table} />
            ))}

            <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.045) }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Approach / working</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {WORKING_PROMPTS[practice.id] || 'Show the grain, the row-count vs distinct-key check, and what differs between the repeated rows before deciding the root cause.'}
              </Typography>
              <TextField
                value={responses[practice.id]?.approach_working || ''}
                onChange={(event) => updateAnswer(practice.id, 'approach_working', event.target.value)}
                placeholder="Start with: Grain = one row per... Then show: row count vs distinct key count, and exactly which columns match or differ between the repeated rows..."
                size="small"
                fullWidth
                multiline
                minRows={4}
              />
            </Box>

            <Box sx={{ overflowX: 'auto', mt: 2 }}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& th': {
                    bgcolor: alpha(theme.palette.primary.main, 0.09),
                    fontWeight: 800,
                  },
                  '& th, & td': {
                    border: `1px solid ${theme.palette.divider}`,
                    px: 1.25,
                    py: 0.9,
                    textAlign: 'left',
                    verticalAlign: 'top',
                  },
                  '& th:first-of-type, & td:first-of-type': {
                    width: { xs: 150, sm: 210 },
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Lens</th>
                    <th>Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {LENSES.map(([key, label]) => {
                    const lensResult = grades[practice.id]?.lensResults?.[key] || null;
                    const missed = lensResult && lensResult.status !== 'correct';
                    return (
                      <tr key={key}>
                        <td>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {lensResult?.status === 'correct' && <CheckCircleIcon color="success" fontSize="small" />}
                            {lensResult?.status === 'partial' && <ErrorOutlineIcon color="warning" fontSize="small" />}
                            {lensResult?.status === 'missed' && <ErrorOutlineIcon color="error" fontSize="small" />}
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {label}
                            </Typography>
                          </Stack>
                        </td>
                        <td>
                          <TextField
                            value={responses[practice.id]?.[key] || ''}
                            onChange={(event) => updateAnswer(practice.id, key, event.target.value)}
                            placeholder={`Type your ${label.toLowerCase()}...`}
                            size="small"
                            fullWidth
                            multiline
                            minRows={key === 'learner_sentence' ? 2 : 1}
                            color={lensResult?.status === 'correct' ? 'success' : lensResult?.status === 'partial' ? 'warning' : 'primary'}
                            focused={Boolean(lensResult)}
                          />
                          {missed && (
                            <Alert severity={lensResult.status === 'partial' ? 'warning' : 'error'} sx={{ mt: 0.75, py: 0 }}>
                              <Typography variant="caption" component="div">
                                Expected direction: {lensResult.expected}
                              </Typography>
                              {lensResult.reason && <Typography variant="caption" component="div">{lensResult.reason}</Typography>}
                            </Alert>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}
